import React, {createElement, Suspense, useCallback, useMemo} from 'react'
import {
  BrowserRouter,
  BrowserRouterProps,
  Redirect,
  Router as RouterOrigin,
  Route,
  Switch,
} from 'react-router-dom'
import {Routes} from 'types/commons'
import {getRoutePath} from 'utils/commons'
import history from 'lib/history'
import {useSelector} from 'lib/redux'
import {RouteMap, RouteMapEntry, ROUTE_MAP} from './RouteMap'
import Middleware from './Middleware'

interface RouteSwitchProps<T> {
  map: RouteMap<T>
  relativePath?: string
  redirectPath?: string
}

interface RouterProps<T> extends BrowserRouterProps {
  basename: Routes
  map: RouteMap<T>
}

function RouteSwitch<T>({
  relativePath = '',
  redirectPath,
  map,
}: RouteSwitchProps<T>) {
  const user = useSelector('user')

  /** Temp add tree validation to force logout old app state */
  const loggedIn = useMemo(() => !!user?.token && !!user?.tree, [user])

  const handleDisableRoute = useCallback(
    ([, {disabled = false}]: [string, RouteMapEntry<any, any>]) => !disabled,
    [],
  )

  const handleFilterAllowedRoutes = useCallback(
    ([, {permission = 'all'}]: [string, RouteMapEntry<any, any>]) =>
      (loggedIn && permission !== 'guest') ||
      (!loggedIn && permission !== 'user'),
    [loggedIn],
  )

  const handleRenderRoute = useCallback(
    ([route, routeMapEntry]: [string, RouteMapEntry<any, any>]) => {
      const param: any = {
        route,
        exact: true,
        relativePath,
        ...routeMapEntry,
      }

      const {path, exact, component, nested, defaultState} = param

      return defaultState === undefined ? (
        <Route key={route} path={`${relativePath}${path}`} exact={exact}>
          {createElement(
            component,
            undefined,
            nested ? (
              <RouteSwitch map={nested} relativePath={path} />
            ) : undefined,
          )}
        </Route>
      ) : (
        createElement(Middleware, param)
      )
    },
    [relativePath],
  )

  const handleRenderRoutes = useMemo(
    () =>
      Object.entries<RouteMapEntry<any, any>>(map)
        .filter(handleFilterAllowedRoutes)
        .filter(handleDisableRoute)
        .map(handleRenderRoute),

    [handleDisableRoute, handleFilterAllowedRoutes, handleRenderRoute, map],
  )

  return (
    <RouterOrigin history={history}>
      <Switch>
        {handleRenderRoutes}
        {redirectPath && <Redirect to={redirectPath} />}
      </Switch>
    </RouterOrigin>
  )
}

function Router<T>({basename, map}: RouterProps<T>) {
  const base = useMemo(() => getRoutePath(basename), [basename])

  return (
    <Suspense fallback={null}>
      <BrowserRouter>
        <RouteSwitch map={map} redirectPath={base} />
      </BrowserRouter>
    </Suspense>
  )
}

export default function RouteStack() {
  return <Router basename="auth_login" map={ROUTE_MAP} />
}
